import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { CalendarIcon, ClockIcon } from '@heroicons/react/outline';

export interface BookISBN {
  id: number;
  title: string;
  slug: string;
  thumbnailMedium: string | null;
  thumbnailBig: string | null;
}

export interface Article {
  id: number;
  file: string | null; // Assuming 'file' could be a string path or null
  title: string;
  content: string;
  slug: string;
  formatted_markdown: string;
  user: string | null; // Adjust based on actual user object structure if available
  bookcopy: string | null; // Adjust based on actual bookcopy structure if available
  bookisbn: BookISBN;
  uid: string;
  is_published: boolean;
  is_private: boolean;
  commentable: 'off' | 'on' | string; // Adjust based on possible values
  notify: 'off' | 'on' | string; // Adjust based on possible values
  sharing: 'public' | 'private' | string; // Adjust based on possible values
  created_at: string; // ISO 8601 date string
  updated_at: string; // ISO 8601 date string
  view_count: number;
  playback_count: number;
  favoritings_count: number;
  comment_count: number;
  read_time: number;
}

type CardSize = 'small' | 'medium' | 'large';
type ColumnCount = 1 | 2 | 3 | 4 | 5 | 6;

interface UserArticlesProps {
  userId?: number;
  bookisbnId?: number;
  limit?: number;
  size?: CardSize;
  cols?: ColumnCount;
  initialData?: Article[];
  isBookTitle: boolean
}

const UserArticles = ({
  userId,
  bookisbnId,
  limit = 10,
  size = 'medium',
  cols = 3,
  initialData = [],
  isBookTitle = true,
}: UserArticlesProps) => {
  const [articles, setArticles] = useState<Article[]>(initialData);
  const [loading, setLoading] = useState(!initialData.length);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticles = async () => {
      if (!userId && !bookisbnId) return;

      try {
        let queryParams = '';

        if (userId) {
          queryParams += `user=${userId}`;
        }

        if (bookisbnId) {
          queryParams += queryParams ? '&' : '';
          queryParams += `bookisbn=${bookisbnId}`;
        }

        if (limit) {
          queryParams += queryParams ? '&' : '';
          queryParams += `limit=${limit}`;
        }

        // Always include the current timestamp to bypass cache for fresh content
        queryParams += queryParams ? '&' : '';
        queryParams += `_t=${Date.now()}`;

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_DJANGO_URL}/api/tracks?${queryParams}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch articles');
        }

        const data = await response.json();
        setArticles(data.results || []);
      } catch (err) {
        setError('Could not load articles');
        console.error('Error fetching articles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();

    // Set up polling for new articles (every 30 seconds)
    const intervalId = setInterval(fetchArticles, 30000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [userId, bookisbnId, limit]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="mt-2 h-24 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (articles.length === 0) {
    return (
      <div className="py-4 text-center text-gray-500">
        <p>Brak artykułów do wyświetlenia</p>
      </div>
    );
  }

  // Define grid classes based on column count
  const getGridClasses = () => {
    const gap = size === 'large' ? 'gap-6' : size === 'small' ? 'gap-4' : 'gap-5';

    switch (cols) {
      case 1:
        return `grid grid-cols-1 ${gap}`;
      case 2:
        return `grid grid-cols-1 sm:grid-cols-2 ${gap}`;
      case 4:
        return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${gap}`;
      case 5:
        return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 ${gap}`;
      case 6:
        return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 ${gap}`;
      case 3:
      default:
        return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${gap}`;
    }
  };

  // Configure content detail level based on size
  const getContentComponents = () => {
    switch (size) {
      case 'small':
        return {
          showDescription: false,
          showMeta: true,
          lineClamp: 'line-clamp-1',
          cardHeight: 'h-32',
          titleClass: 'text-base'
        };
      case 'large':
        return {
          showDescription: true,
          showMeta: true,
          lineClamp: 'line-clamp-3',
          cardHeight: 'h-64',
          titleClass: 'text-xl'
        };
      case 'medium':
      default:
        return {
          showDescription: true,
          showMeta: true,
          lineClamp: 'line-clamp-2',
          cardHeight: 'h-52',
          titleClass: 'text-lg'
        };
    }
  };

  const contentConfig = getContentComponents();

  return (
    <div className={getGridClasses()}>
      {articles.map((article) => (
        <Link
          href={`/a/${article.slug}`}
          key={article.id}
          className="block"
        >
          <div className={`group flex flex-col ${contentConfig.cardHeight} overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md`}>
            <div className="flex flex-1 flex-col p-4">
              <h3 className={`font-semibold ${contentConfig.titleClass} text-gray-800 leading-tight mb-2 group-hover:text-gray-900`}>
                {article.title}
              </h3>

              {contentConfig.showDescription && (
                <p className={`text-sm text-gray-500 ${contentConfig.lineClamp} mb-4`}>
                  {article.content || "Przeczytaj więcej..."}
                </p>
              )}

              {contentConfig.showMeta && (
                <div className="mt-auto flex justify-between items-center text-xs text-gray-500">
                  <time className='flex' dateTime={article.created_at}>
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {new Date(article.created_at).toLocaleDateString('pl-PL', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </time>
                  {isBookTitle && article.bookisbn && (
                    <span className="ml-3">{article.bookisbn.title} </span>
                  )}
                  <span className="flex"><ClockIcon className="h-4 w-4 mr-1" />{`${article.read_time || 1} min`} </span>
                </div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default UserArticles;